import { currencyConfiguration } from './currency-config';
import * as custom from './marketplace-custom-config.js';
import {
  defaultMCC,
  stripeCountryDetails,
  stripePaymentMethodTypes,
  stripePublishableKey,
  stripeSavablePaymentMethodTypes,
} from './stripe-config';

const env = process.env.REACT_APP_ENV;
const dev = process.env.REACT_APP_ENV === 'development';

// If you want to change the language, remember to also change the
// locale data and the messages in the app.js file.
const locale = 'de';
const fallbackLocale = 'en';

const languages = {
  de: 'de.json',
  en: 'en.json',
  fr: 'fr.json',
  it: 'it.json',
  es: 'es.json',
  sv: 'sv.json',
  no: 'no.json',
};

const i18n = {
  /*
    0: Sunday
    1: Monday
    ...
    6: Saturday
  */
  firstDayOfWeek: 1,
};

//New Policy changes
const newPolicyChangesDate = new Date('2022-03-02T12:00:00Z');

//Configuration for Provissions
const providerCommissionPercentage = -15;
const providerDiscountCommissionPercentage = 0;
const customerCommissionPercentage = 30;

//Configuration of the default guest count for a listing
const defaultGuestCount = parseInt(process.env.REACT_APP_DEFAULT_GUEST_COUNT);

// Should search results be ordered by distance to origin.
// NOTE: If this is set to true add parameter 'origin' to every location in default-location-searches.js
// Without the 'origin' parameter, search will not work correctly
// NOTE: Keyword search and ordering search results by distance can't be used at the same time. You can turn keyword
// search off by removing keyword filter config from filters array in marketplace-custom-config.js
const sortSearchByDistance = false;

// API supports custom processes to be used in booking process.
// We need to specify it when we are initiating a new order
// (or fetching price information by calling 'initiateSpeculative' endpoint).
//
// In a way, 'processAlias' defines which transaction process (or processes)
// this particular web application is able to handle.
const bookingProcessAlias = 'default-booking-process/release-1';

// The transaction line item code for the main unit type in bookings.
//
// Possible values: ['line-item/night', 'line-item/day', 'line-item/units';]
//
// Note 1: This 'bookingUnitType' variable affects only web app.
//         If you are using privileged transitions (which is used by the default process),
//         you also need to configure unit type in API server: server/api-util/lineItems.js
//
// Note 2: Translations will use different translation keys for night, day or unit
//         depending on the value chosen.
const bookingUnitType = 'line-item/night';

const customLineItemMinimumPriceSubUnits = 100;
const customLineItemMaximumPriceSubUnits = 10000;

// Should the application fetch available time slots (currently defined as
// start and end dates) to be shown on listing page.
const enableAvailability = process.env.REACT_APP_AVAILABILITY_ENABLED === 'true';

// marketplace-web@P1 (ADR-002): route login through the server-side proxy
// (POST /api/auth/login, lazy credential migration). Default OFF — the client
// keeps using sdk.login(). Server may still answer 409 PROXY_LOGIN_DISABLED,
// which triggers the sdk.login fallback (rollback lever).
const proxyLoginEnabled = process.env.REACT_APP_PROXY_LOGIN_ENABLED === 'true';

// A maximum number of days forwards during which a booking can be made.
// This is limited due to Stripe holding funds up to 90 days from the
// moment they are charged. Also note that available time slots can only
// be fetched for 180 days in the future.
const dayCountAvailableForBooking = 365;

// To pass environment variables to the client app in the build
// script, react-scripts (and the sharetribe-scripts fork of
// react-scripts) require using the REACT_APP_ prefix to avoid
// exposing server secrets to the client side.
const sdkClientId = process.env.REACT_APP_SHARETRIBE_SDK_CLIENT_ID;
const sdkBaseUrl = process.env.REACT_APP_SHARETRIBE_SDK_BASE_URL;
const sdkTransitVerbose = process.env.REACT_APP_SHARETRIBE_SDK_TRANSIT_VERBOSE === 'true';

// Marketplace currency.
// It should match one of the currencies listed in currency-config.js
const currencyConf = process.env.REACT_APP_SHARETRIBE_MARKETPLACE_CURRENCY || 'EUR';
const currency = currencyConf ? currencyConf.toUpperCase() : currencyConf;

// Currency formatting options.
// See: https://github.com/yahoo/react-intl/wiki/API#formatnumber
const currencyConfig = currencyConfiguration(currency);

// Listing minimum price in currency sub units, e.g. cents.
// 0 means no restriction to the price
const listingMinimumPriceSubUnits = 500;
const listingMaximumPriceSubUnits = 3000;
// Camping grounds (publicData.listingCategory === 'campingGround') get a higher ceiling.
const listingMaximumPriceSubUnitsCampingGround = 8000;

// Sentry DSN (Data Source Name), a client key for authenticating calls to Sentry
const sentryDsn = process.env.REACT_APP_SENTRY_DSN;

// If webapp is using SSL (i.e. it's behind 'https' protocol)
const usingSSL = process.env.REACT_APP_SHARETRIBE_USING_SSL === 'true';

// Address information is used in SEO schema for Organization (http://schema.org/PostalAddress)
const addressCountry = 'DE';
const addressRegion = 'Germany';
const postalCode = '33739';
const streetAddress = 'Am Südhang 8';

// Canonical root url is needed in social media sharing and SEO optimization purposes.
const canonicalRootURL = process.env.REACT_APP_CANONICAL_ROOT_URL;

// Site title is needed in meta tags (bots and social media sharing reads those)
const siteTitle = 'VanSite';

// Twitter handle is needed in meta tags (twitter:site). Start it with '@' character
const siteTwitterHandle = '@VanSite_eu';

// Instagram page is used in SEO schema (http://schema.org/Organization)
const siteInstagramPage = 'https://www.instagram.com/vansite.eu/';

// Facebook page is used in SEO schema (http://schema.org/Organization)
const siteFacebookPage = 'https://www.facebook.com/VanSite.eu';

// Tiktok page is used in SEO schema (http://schema.org/Organization)
const siteTiktokPage = 'https://www.tiktok.com/@vansite';

// LinkedIn page is used in SEO schema (http://schema.org/Organization)
const siteLinkedInPage = 'https://linkedin.com/company/vansite';

// YouTube page is used in SEO schema (http://schema.org/Organization)
const siteYouTubePage = 'https://www.youtube.com/@vansiteeu';

// Social logins & SSO

// Note: Facebook app id is also used for tracking:
// Facebook counts shares with app or page associated by this id
// Currently it is unset, but you can read more about fb:app_id from
// https://developers.facebook.com/docs/sharing/webmasters#basic
// You should create one to track social sharing in Facebook
const facebookAppId = process.env.REACT_APP_FACEBOOK_APP_ID;

const androidStoreLink = 'https://play.google.com/store/apps/details?id=eu.vansite.mobile';
const iosStoreLink = 'https://apps.apple.com/de/app/vansite-camping-stellpl%C3%A4tze/id1573760276';

const maps = {
  mapboxAccessToken: process.env.REACT_APP_MAPBOX_ACCESS_TOKEN,

  // The location search input can be configured to show default
  // searches when the user focuses on the input and hasn't yet typed
  // anything. This reduces typing and avoids too many Geolocation API
  // calls for common searches.
  search: {
    // When enabled, the first suggestion is "Current location" that
    // uses the browser Geolocation API to query the user's current
    // location.
    suggestCurrentLocation: process.env.REACT_APP_DEFAULT_SEARCHES_ENABLED === 'true',

    // Distance in meters for calculating the bounding box around the
    // current location.
    currentLocationBoundsDistance: 10000,
    currentLocationPathBoundsDistance: 10000,

    distanceSearchOffsetX: 27500,
    distanceSearchOffsetY: 37500,

    locationBoundsOffset: 25000,

    // Limit location autocomplete to a one or more countries
    // using ISO 3166 alpha 2 country codes separated by commas.
    // If you want to limit the autocomplete, uncomment this value:
    countryLimit: [
      'DE', //Germany
      'BE', //Belgian
      'BG', //Bulgaria
      'CZ', //Czechia
      'DK', //Denmark
      'EE', //Estonia
      'IE', //Ireland
      'GR', //Greece
      'ES', //Spain
      'FR', //France
      'HR', //Croatia
      'IT', //Italy
      'CY', //Cyprus
      'LV', //Latvia
      'LT', //Lithuania
      'LU', //Luxembourg
      'HU', //Hungary
      'MT', //Malta
      'NL', //Netherlands
      'AT', //Austria
      'PL', //Poland
      'PT', //Portugal
      'RO', //Romania
      'SI', //Slovenia
      'SK', //Slovakia
      'FI', //Finland
      'SE', //Sweden
      'GB', //United Kingdom
      'IS', //Iceland
      'LI', //Liechtenstein
      'NO', //Norway
      'CH', //Switzerland
      'TR', //Turkey
      'UA', //Ukraine
      // 'RU', //Russia
      // 'BY', //Belarus
      'GB', //United Kingdom
      'ME', //Montenegro
      'MK', //Macedonia
      'AL', //Albania
      'RS', //Serbia
      'BA', //Bosnia and Herzegovina
      'AM', //Armenia
      'AZ', //Azerbaijan
      'GE', //Georgia
      'MD', //Moldova
    ],

    poiCategories: ['tourist_attraction', 'park', 'zoo', 'aquarium'],

    sortingOrder: {
      country: 1,
      region: 2,
      poi: 3,
      district: 4,
      place: 5,
      postcode: 6,
    },

    updateDistanceInterval: 2500,
  },

  // When fuzzy locations are enabled, coordinates on maps are
  // obfuscated randomly around the actual location.
  //
  // NOTE: This only hides the locations in the UI level, the actual
  // coordinates are still accessible in the HTTP requests and the
  // Redux store.
  fuzzy: {
    enabled: true,

    // Amount of maximum offset in meters that is applied to obfuscate
    // the original coordinates. The actual value is random, but the
    // obfuscated coordinates are withing a circle that has the same
    // radius as the offset.
    offset: 1000,

    // Default zoom level when showing a single circle on a Map. Should
    // be small enough so the whole circle fits in.
    defaultZoomLevel: 13,

    // Color of the circle on the Map component.
    circleColor: '#c0392b',
  },

  // Custom marker image to use in the Map component.
  //
  // NOTE: Not used if fuzzy locations are enabled.
  customMarker: {
    enabled: false,

    // Publicly accessible URL for the custom marker image.
    //
    // The easiest place is /public/static/icons/ folder, but then the
    // marker image is not available while developing through
    // localhost.
    url: encodeURI(`${canonicalRootURL}/static/icons/vansite-map-marker.svg`),

    // Dimensions of the marker image.
    width: 32,
    height: 32,

    // Position to anchor the image in relation to the coordinates,
    // ignored when using Mapbox.
    anchorX: 16,
    anchorY: 32,
  },
};

const amountOfSeats = {
  min: 1,
  max: 100,
};

const blog = {
  limit: 12,
  order: '-sys.updatedAt,-fields.publishDate',
};

const firebase = (appIdType = 'web') => {
  const androidAppId = process.env.REACT_APP_GOOGLE_FIREBASE_APP_ID_ANDROID;
  const iOSAppId = process.env.REACT_APP_GOOGLE_FIREBASE_APP_ID_IOS;
  const webAppId = process.env.REACT_APP_GOOGLE_FIREBASE_APP_ID_WEB;
  const androidApiKey = process.env.REACT_APP_GOOGLE_FIREBASE_API_KEY_ANDROID;
  const iOSApiKey = process.env.REACT_APP_GOOGLE_FIREBASE_API_KEY_IOS;
  const webApiKey = process.env.REACT_APP_GOOGLE_FIREBASE_API_KEY_WEB;
  return {
    apiKey: appIdType === 'web' ? webApiKey : appIdType === 'android' ? androidApiKey : iOSApiKey,
    authDomain: process.env.REACT_APP_GOOGLE_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_GOOGLE_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_GOOGLE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_GOOGLE_FIREBASE_MESSAGING_SENDER_ID,
    appId: appIdType === 'web' ? webAppId : appIdType === 'android' ? androidAppId : iOSAppId,
    measurementId: process.env.REACT_APP_GOOGLE_FIREBASE_MEASUREMENT_ID,
  };
};

const google = {
  recaptcha: process.env.REACT_APP_GOOGLE_RECAPTCHA_WEB_KEY,
};

const facebook = {
  pixel: {
    id: process.env.REACT_APP_FACEBOOK_PIXEL_ID,
    options: {
      autoConfig: true,
      debug: dev,
    },
  },
};

// defaultValues for listing creation
const listing = {
  publicData: {
    hidden: false,
    verified: 0,
  },
  privateData: {},
};

const oneSignal = {
  appId: process.env.REACT_APP_ONE_SIGNAL_APP_ID,
};

const annualPass = {
  lightPriceThreshold: process.env.ANNUAL_PASS_PLUS_LISTING_PRICE_THRESHOLD
    ? parseInt(process.env.ANNUAL_PASS_PLUS_LISTING_PRICE_THRESHOLD, 10)
    : 2000,
  bookingLimit: process.env.REACT_APP_ANNUAL_PASS_BOOKING_LIMIT
    ? parseInt(process.env.REACT_APP_ANNUAL_PASS_BOOKING_LIMIT, 10)
    : 3,
  simultaneouslyBookingLimit: process.env.REACT_APP_ANNUAL_PASS_SIMULTANEOUSLY_BOOKING_LIMIT
    ? parseInt(process.env.REACT_APP_ANNUAL_PASS_SIMULTANEOUSLY_BOOKING_LIMIT, 10)
    : 4,
  sameProviderBookingLimit: process.env.REACT_APP_ANNUAL_PASS_SAME_PROVIDER_BOOKING_LIMIT
    ? parseInt(process.env.REACT_APP_ANNUAL_PASS_SAME_PROVIDER_BOOKING_LIMIT, 10)
    : 7,
};

/**
 * Loyalty program configuration.
 * @typedef {object} LoyaltyProgramm -LoyaltyProgramConfig
 * @property {object} loyaltyProgramm.voucherCampaigns - The voucher campaigns.
 * @property {string} loyaltyProgramm.voucherCampaigns.referralProgramId - The referral program ID.
 * @property {string} loyaltyProgramm.voucherCampaigns.loyaltyProgramId - The loyalty program ID.
 * @property {string} loyaltyProgramm.voucherCampaigns.5EuroVoucher - The 5 euro voucher campaign ID.
 * @property {string} loyaltyProgramm.voucherCampaigns.15EuroVoucher - The 15 euro voucher campaign ID.
 * @property {string} loyaltyProgramm.voucherCampaigns.25EuroVoucher - The 25 euro voucher campaign ID.
 */
const loyaltyProgramm = {
  voucherCampaigns: {
    referralProgramId: 'camp_8kY4OvqGyzhYkNxCt81IBEZb',
    loyaltyProgramId: 'camp_gyO3wme9e4RGpkeo7dVF37aC',
    '5EuroVoucher': 'camp_uHA7GPhD3IrGF4Z221FdZ7n7',
    '15EuroVoucher': 'camp_DSbMefxVyCjteKpFaokpl6nT',
    '25EuroVoucher': 'camp_uKbu17QkcX5aCf8qLr3aC0L1',
  },
};
/**
 * @typedef {object} SdkConfig
 * @property {string} clientId - The SDK client ID.
 * @property {string} baseUrl - The base URL for the SDK.
 * @property {boolean} transitVerbose - Flag to enable verbose logging in SDK transit.
 */

/**
 * @typedef {object} AddressConfig
 * @property {string} addressCountry - The country of the address.
 * @property {string} addressRegion - The region or state of the address.
 * @property {string} postalCode - The postal code of the address.
 * @property {string} streetAddress - The street address.
 */

/**
 * @typedef {object} StripeConfig
 * @property {string} defaultMCC - The default Merchant Category Code (MCC).
 * @property {string} publishableKey - The Stripe publishable key.
 * @property {Array<string>} supportedCountries - List of supported countries for Stripe.
 * @property {Array<string>} paymentMethodTypes - List of payment method types supported by Stripe.
 * @property {Array<string>} savablePaymentMethods - List of savable payment methods excluding PayPal.
 * @property {object} priceIds - All Price
 */

/**
 * NOTE: only expose configuration that should be visible in the
 * client side, don't add any server secrets in this file.
 *
 * @typedef {object} Config
 * @property {any} firebase - Firebase configuration object.
 * @property {any} google - Google configuration object.
 * @property {any} facebook - Facebook configuration object.
 * @property {string} env - The environment (e.g., production, development).
 * @property {boolean} dev - Flag indicating if in development mode.
 * @property {number} defaultGuestCount - Default guest count for booking.
 * @property {string} locale - Default locale setting.
 * @property {string} fallbackLocale - Fallback locale setting.
 * @property {Array<string>} languages - Supported languages list.
 * @property {string} bookingProcessAlias - Alias for the booking process.
 * @property {string} bookingUnitType - Type of booking unit.
 * @property {boolean} enableAvailability - Flag to enable availability checking.
 * @property {number} dayCountAvailableForBooking - Number of days available for booking.
 * @property {any} i18n - Internationalization configuration object.
 * @property {SdkConfig} sdk - SDK configuration object.
 * @property {boolean} sortSearchByDistance - Flag to enable sorting search results by distance.
 * @property {string} currency - Default currency code.
 * @property {any} currencyConfig - Currency configuration object.
 * @property {any} listing - Listing configuration object.
 * @property {number} listingMinimumPriceSubUnits - Minimum price for listing in sub-units.
 * @property {number} listingMaximumPriceSubUnits - Maximum price for listing in sub-units.
 * @property {number} listingMaximumPriceSubUnitsCampingGround - Maximum price for campingGround listings in sub-units.
 * @property {number} customLineItemMinimumPriceSubUnits - Minimum price for custom line items in sub-units.
 * @property {number} customLineItemMaximumPriceSubUnits - Maximum price for custom line items in sub-units.
 * @property {StripeConfig} stripe - Stripe payment configuration.
 * @property {string} canonicalRootURL - The canonical root URL of the site.
 * @property {AddressConfig} address - Address configuration object.
 * @property {string} siteTitle - The title of the site.
 * @property {string} siteFacebookPage - The Facebook page URL of the site.
 * @property {string} siteInstagramPage - The Instagram page URL of the site.
 * @property {string} siteTwitterHandle - The Twitter handle of the site.
 * @property {number} providerCommissionPercentage - Commission percentage for providers.
 * @property {number} providerDiscountCommissionPercentage - Discount commission percentage for providers.
 * @property {number} customerCommissionPercentage - Commission percentage for customers.
 * @property {string} newPolicyChangesDate - The date of new policy changes.
 * @property {string} facebookAppId - The Facebook App ID.
 * @property {string} sentryDsn - The DSN for Sentry error tracking.
 * @property {boolean} usingSSL - Flag indicating if SSL is being used.
 * @property {any} maps - Maps configuration object.
 * @property {any} custom - Custom configuration object.
 * @property {number} amountOfSeats - The number of seats available.
 * @property {string} androidStoreLink - The link to the Android app store.
 * @property {string} iosStoreLink - The link to the iOS app store.
 * @property {string} blog - The blog URL.
 * @property {any} oneSignal - OneSignal configuration object.
 * @property {any} annualPass - Annual pass configuration object.
 * @property {any} translationDBParams - Translation database parameters.
 * @property {LoyaltyProgramm} loyaltyProgramm - Loyalty program configuration object.
 */
const config = {
  firebase,
  google,
  facebook,
  env,
  dev,
  defaultGuestCount,
  locale,
  fallbackLocale,
  languages,
  bookingProcessAlias,
  bookingUnitType,
  enableAvailability,
  proxyLoginEnabled,
  dayCountAvailableForBooking,
  i18n,
  sdk: {
    clientId: sdkClientId,
    baseUrl: sdkBaseUrl,
    transitVerbose: sdkTransitVerbose,
  },
  sortSearchByDistance,
  currency,
  currencyConfig,
  listing,
  listingMinimumPriceSubUnits,
  listingMaximumPriceSubUnits,
  listingMaximumPriceSubUnitsCampingGround,
  customLineItemMinimumPriceSubUnits,
  customLineItemMaximumPriceSubUnits,
  stripe: {
    defaultMCC: defaultMCC,
    publishableKey: stripePublishableKey,
    supportedCountries: stripeCountryDetails,
    paymentMethodTypes: stripePaymentMethodTypes,
    savablePaymentMethods: stripeSavablePaymentMethodTypes,
  },
  canonicalRootURL,
  address: {
    addressCountry,
    addressRegion,
    postalCode,
    streetAddress,
  },
  siteTitle,
  siteFacebookPage,
  siteInstagramPage,
  siteTwitterHandle,
  siteTiktokPage,
  siteLinkedInPage,
  siteYouTubePage,
  providerCommissionPercentage,
  providerDiscountCommissionPercentage,
  customerCommissionPercentage,
  newPolicyChangesDate,
  facebookAppId,
  sentryDsn,
  usingSSL,
  maps,
  custom,
  amountOfSeats,
  androidStoreLink,
  iosStoreLink,
  blog,
  oneSignal,
  annualPass,
  loyaltyProgramm,
};

export default config;
